export interface NavItem {
  to: string;
  label: string;
  /** Roles that see this nav entry — spec §5: "staff see bookings and
   * check-in only", so anything beyond that is owner-only. */
  roles: Array<'owner' | 'reception'>;
}

export const BUSINESS_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', roles: ['owner', 'reception'] },
  { to: '/check-in', label: 'Check-in', roles: ['owner', 'reception'] },
  { to: '/bookings', label: 'Bookings', roles: ['owner', 'reception'] },
  { to: '/bookings/calendar', label: 'Calendar', roles: ['owner', 'reception'] },
  { to: '/rates', label: 'Rates', roles: ['owner'] },
  { to: '/listings', label: 'Listings', roles: ['owner'] },
  { to: '/settlement', label: 'Settlement', roles: ['owner'] },
  { to: '/statements', label: 'Statements', roles: ['owner'] },
  { to: '/reviews', label: 'Reviews', roles: ['owner'] },
  { to: '/verification', label: 'Verification', roles: ['owner'] },
  { to: '/team', label: 'Team', roles: ['owner'] },
];

/** Shown only when the selected property is a restaurant (`__root.tsx` reads
 * the category) — table hours, party size, notice and auto-accept mean
 * nothing to a lodge or a guide. */
export const RESTAURANT_NAV: NavItem[] = [{ to: '/reservations', label: 'Reservations', roles: ['owner'] }];

/** Business Pro / Featured / Verified badge / channel sync (spec §5.11) —
 * built as seams only, hidden from nav until BOTH `flags.hostProducts` is
 * on AND `entity.namibia_ltd` is true (plan §9b; 20260926000009's ruling).
 * `__root.tsx` reads the live gate via
 * `lib/data/featureFlags.ts#useHostProductsGate` (Task 24) rather than a
 * hardcoded constant — hardcoded off at launch since both the flag and the
 * entity are seeded false. */
export const HOST_PRODUCTS_NAV: NavItem[] = [
  { to: '/pro', label: 'Business Pro', roles: ['owner'] },
  { to: '/featured', label: 'Featured placement', roles: ['owner'] },
  { to: '/verified-badge', label: 'Verified badge', roles: ['owner'] },
  { to: '/channels', label: 'Channel sync', roles: ['owner'] },
];

const PUBLIC_BUSINESS_PATHS = ['/sign-in', '/sign-in/mfa', '/sign-in/passkey', '/invite/accept'];

export function isPublicBusinessPath(pathname: string): boolean {
  return PUBLIC_BUSINESS_PATHS.includes(pathname);
}

/** Pure so it's Vitest-testable without rendering the shell (spec §5: staff
 * "see bookings and check-in only"). `role === null` (memberships still
 * loading, or the account belongs to no property yet) shows the minimal
 * nav — the items every role gets — rather than the full owner nav (fix
 * round 1, item 4: showing every item, including Team and Settlement,
 * before the role is actually known leaked owner-only entries to a
 * reception account for one render). */
export function filterNavByRole(items: NavItem[], role: 'owner' | 'reception' | null): NavItem[] {
  return items.filter((item) => item.roles.includes(role ?? 'reception'));
}
